import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSellerReportDetail, handleSellerReport } from '../../services/api';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  Trash2,
  Eye,
  Store,
  User,
  Calendar,
  Clock,
  Package,
  DollarSign,
  MessageSquare,
  Mail,
  Phone,
  MapPin,
  TrendingUp,
  Flag,
  History,
  Star,
  ShoppingBag,
  Users,
  Ban,
  FileWarning,
  Send,
  X,
  Check,
  RefreshCw,
  AlertCircle,
  Info,
  Activity,
  ChevronRight,
  Download,
  BarChart2,
  Grid,
  List,
  Filter,
  Search
} from 'lucide-react';

function SellerReportDetail({ admin }) {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Seller-specific actions
  const actionOptions = [
    { 
      value: 'dismiss', 
      label: 'Dismiss Report', 
      icon: <CheckCircle className="w-4 h-4" />, 
      color: 'text-green-400',
      bg: 'bg-green-900/30',
      description: 'Mark this report as false or invalid'
    },
    { 
      value: 'warn', 
      label: 'Warn Seller', 
      icon: <AlertTriangle className="w-4 h-4" />, 
      color: 'text-yellow-400',
      bg: 'bg-yellow-900/30',
      description: 'Send warning to seller about their conduct'
    },
    { 
      value: 'suspend', 
      label: 'Suspend Account', 
      icon: <Shield className="w-4 h-4" />, 
      color: 'text-orange-400',
      bg: 'bg-orange-900/30',
      description: 'Temporarily suspend seller account'
    },
    { 
      value: 'reject_application', 
      label: 'Reject Application', 
      icon: <Ban className="w-4 h-4" />, 
      color: 'text-red-400',
      bg: 'bg-red-900/30',
      description: 'Reject seller application permanently'
    }
  ];

  const statusConfig = {
    pending: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <Clock className="w-4 h-4" />, 
      label: 'Pending Review' 
    },
    reviewed: { 
      color: 'text-blue-400', 
      bg: 'bg-blue-900/30', 
      border: 'border-blue-800/50', 
      icon: <CheckCircle className="w-4 h-4" />, 
      label: 'Reviewed' 
    },
    dismissed: { 
      color: 'text-gray-400', 
      bg: 'bg-gray-700', 
      border: 'border-gray-600', 
      icon: <XCircle className="w-4 h-4" />, 
      label: 'Dismissed' 
    }
  };

  const severityConfig = {
    high: { 
      color: 'text-red-400', 
      bg: 'bg-red-900/30', 
      border: 'border-red-800/50', 
      icon: <AlertTriangle className="w-4 h-4" />, 
      label: 'High' 
    },
    medium: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <AlertTriangle className="w-4 h-4" />, 
      label: 'Medium' 
    },
    low: { 
      color: 'text-green-400', 
      bg: 'bg-green-900/30', 
      border: 'border-green-800/50', 
      icon: <AlertTriangle className="w-4 h-4" />, 
      label: 'Low' 
    }
  };

  const fetchReportDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getSellerReportDetail(reportId);
      
      if (response.success && response.data) {
        setReport(response.data);
      } else {
        setReport(response);
      }
    } catch (error) {
      setError('Failed to load seller report details: ' + error.message);
      console.error('Error fetching seller report details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportDetail();
  }, [reportId]);

  const handleAction = async (action, adminNotes = '', severity = 'medium', sendEmail = true) => {
    try {
      setActionLoading(true);
      setError('');
      await handleSellerReport(reportId, action, adminNotes, severity, sendEmail);
      setSuccessMessage('Action completed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReportDetail();
      setShowActionModal(false);
    } catch (error) {
      setError('Failed to complete action: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₦0.00';
    return `₦${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-48 mb-4"></div>
                  <div className="space-y-4">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-32 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(2)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-gray-600 text-6xl mb-6">🔍</div>
          <h3 className="text-xl font-medium text-white mb-2">Seller report not found</h3>
          <p className="text-gray-400 mb-6">The report you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/reports/sellers')}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Seller Reports
          </button>
        </div>
      </div>
    );
  }

  const status = statusConfig[report.status] || statusConfig.pending;
  const severity = severityConfig[report.severity] || severityConfig.medium;

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <CheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/reports/sellers"
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                <span className="text-[#ff660e]">Seller Report</span> Details
              </h1>
              <p className="text-gray-400 mt-1">Report #{report.report_id} • {report.store_name}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${status.bg} ${status.color} ${status.border}`}>
              {status.icon}
              <span className="ml-2">{status.label}</span>
            </span>
            
            {report.severity && (
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${severity.bg} ${severity.color} ${severity.border}`}>
                {severity.icon}
                <span className="ml-2">{severity.label}</span>
              </span>
            )}
            
            <button
              onClick={() => fetchReportDetail()}
              disabled={loading}
              className="p-2 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Report & Seller Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Report Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-900/30 flex items-center justify-center">
                <Flag className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Report Information</h2>
                <p className="text-sm text-gray-400">Complete report details and evidence</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Report Summary */}
              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Report ID</p>
                    <p className="font-bold text-white text-lg">#{report.report_id}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Reported Date</p>
                    <p className="font-medium text-gray-300">{formatDate(report.created_at)}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Reason</p>
                    <p className="font-medium text-gray-300">{report.reason}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Days Pending</p>
                    <p className="font-medium text-yellow-400">{report.days_pending || 0} days</p>
                  </div>
                </div>
                
                {report.description && (
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-gray-400" />
                      <p className="text-sm font-medium text-gray-300">Description</p>
                    </div>
                    <p className="text-gray-300 whitespace-pre-wrap bg-gray-800/50 rounded-lg p-3">
                      {report.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Reporter Information */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white">Reporter Information</h3>
                </div>
                <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
                  <div className="flex items-center space-x-4">
                    {report.reporter?.profile_picture ? (
                      <img
                        src={report.reporter.profile_picture}
                        alt={report.reporter.first_name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-blue-800/50"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center border-2 border-blue-800/50">
                        <User className="w-8 h-8 text-blue-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-white">
                            {report.reporter_first_name} {report.reporter_last_name}
                          </p>
                          <p className="text-sm text-blue-300">{report.reporter_email}</p>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/users/${report.reported_by}`)}
                          className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center"
                        >
                          <Eye className="mr-1 w-4 h-4" />
                          View Profile
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="text-xs text-gray-400">
                          Member since {report.reporter?.joined_date ? formatDate(report.reporter.joined_date) : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 text-right">
                          Report Count: {report.reporter?.report_count || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Seller Store Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-orange-900/30 flex items-center justify-center">
                <Store className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Store Information</h2>
                <p className="text-sm text-gray-400">Seller store details and statistics</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Store Logo */}
                {report.store_logo_url ? (
                  <img
                    src={report.store_logo_url}
                    alt={report.store_name}
                    className="w-24 h-24 object-cover rounded-lg border border-gray-600"
                  />
                ) : (
                  <div className="w-24 h-24 bg-orange-900/30 rounded-lg flex items-center justify-center border border-gray-600">
                    <Store className="w-12 h-12 text-orange-400" />
                  </div>
                )}
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">{report.store_name}</h3>
                  <p className="text-gray-400 mb-4">{report.store_description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-700/50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Rating</p>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="font-bold text-white">{parseFloat(report.seller_rating || 0).toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="bg-gray-700/50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Total Sales</p>
                      <p className="font-bold text-white">{report.total_sales || 0}</p>
                    </div>
                    <div className="bg-gray-700/50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Joined</p>
                      <p className="font-medium text-gray-300">{formatDate(report.seller_since)}</p>
                    </div>
                    <div className="bg-gray-700/50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Active Listings</p>
                      <p className="font-bold text-white">{report.active_listings || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-700 pt-6">
                <div className="flex items-center space-x-2 mb-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <h4 className="font-medium text-white">Store Details</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium text-gray-300">{report.store_location || 'Not specified'}</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <p className="font-medium text-gray-300">{report.seller_phone || 'Not provided'}</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Delivery Options</p>
                    <p className="font-medium text-gray-300">{report.delivery_options || 'Standard'}</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Business Hours</p>
                    <p className="font-medium text-gray-300">
                      {report.is_24_hours === '1' ? '24/7' : `${report.opening_time} - ${report.closing_time}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Seller's Active Listings */}
          {report.seller_listings && report.seller_listings.length > 0 && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center">
                  <Package className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Seller's Active Listings</h2>
                  <p className="text-sm text-gray-400">{report.seller_listings.length} active listings</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.seller_listings.map((listing) => (
                  <div key={listing.listing_id} className="bg-gray-700/30 border border-gray-600 rounded-lg p-4 hover:bg-gray-700/50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-white mb-1">{listing.title}</h4>
                        <p className="text-lg font-bold text-green-400 mb-2">
                          {formatCurrency(listing.price)}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {listing.view_count || 0} views
                          </span>
                          <span>
                            {formatDate(listing.created_at)}
                          </span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        listing.status === 'active' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                      }`}>
                        {listing.status}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/admin/listings/${listing.listing_id}`)}
                      className="mt-3 w-full text-sm px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-gray-300 rounded-lg transition-colors flex items-center justify-center"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Listing
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Similar Reports for This Seller */}
          {report.similar_reports && report.similar_reports.length > 0 && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
                  <History className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Similar Reports</h2>
                  <p className="text-sm text-gray-400">Other reports for this seller</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {report.similar_reports.map((similarReport) => (
                  <div key={similarReport.report_id} className="bg-gray-700/30 border border-gray-600 rounded-lg p-4 hover:bg-gray-700/50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white">Report #{similarReport.report_id}</p>
                        <p className="text-sm text-gray-400">{similarReport.reason}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          By {similarReport.reporter_first_name} {similarReport.reporter_last_name}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        similarReport.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                        similarReport.status === 'reviewed' ? 'bg-blue-900/30 text-blue-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>
                        {similarReport.status.charAt(0).toUpperCase() + similarReport.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {formatDate(similarReport.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Actions & Quick Info */}
        <div className="space-y-6">
          {/* Seller Personal Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Personal Information</h2>
                <p className="text-sm text-gray-400">Seller personal details</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {report.seller?.profile_picture ? (
                  <img
                    src={report.seller.profile_picture}
                    alt={report.seller.first_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-blue-800/50"
                  />
                ) : (
                  <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center border-2 border-blue-800/50">
                    <User className="w-8 h-8 text-blue-400" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-white">
                    {report.seller_first_name} {report.seller_last_name}
                  </p>
                  <p className="text-sm text-gray-400">{report.seller_email}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">{report.seller_email}</span>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">
                    Joined {formatDate(report.seller_joined)}
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                  <ShoppingBag className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">{report.total_sales || 0} total sales</span>
                </div>
              </div>
              
              <button
                onClick={() => navigate(`/admin/sellers/${report.seller_id}`)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border border-blue-800/50 rounded-lg transition-all duration-200 font-medium hover:scale-[1.02]"
              >
                <Eye className="w-4 h-4" />
                <span>View Full Seller Profile</span>
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          {report.status === 'pending' && admin?.role === 'super_admin' && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h2 className="font-bold text-white mb-6">Quick Actions</h2>
              <p className="text-sm text-gray-400 mb-4">Requires Admin role</p>
              
              <div className="space-y-3">
                {actionOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSelectedAction(option.value);
                      setShowActionModal(true);
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border ${option.bg} ${option.color} hover:opacity-90 transition-all duration-200 hover:scale-[1.02]`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${option.bg}`}>
                        {option.icon}
                      </div>
                      <div className="text-left">
                        <span className="font-medium block">{option.label}</span>
                        <span className="text-xs text-gray-400">{option.description}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          {report.admin_notes && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                  <Info className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Admin Notes</h2>
                  <p className="text-sm text-gray-400">Previous moderation notes</p>
                </div>
              </div>
              <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4">
                <p className="text-yellow-300 whitespace-pre-wrap">{report.admin_notes}</p>
                {report.reviewed_by_admin && (
                  <p className="text-xs text-gray-400 mt-2">
                    Reviewed by: {report.reviewed_by_admin} on {formatDate(report.reviewed_at)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Statistics Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="font-bold text-white mb-6">Quick Stats</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Flag className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Reports</p>
                    <p className="text-xl font-bold text-white">{report.pending_reports || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Active Listings</p>
                    <p className="text-xl font-bold text-white">{report.active_listings || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-900/30 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Days Pending</p>
                    <p className="text-xl font-bold text-yellow-400">{report.days_pending || 0} days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Report Metadata */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="font-bold text-white mb-6">Report Metadata</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Report ID:</span>
                <span className="font-medium text-white">#{report.report_id}</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Created:</span>
                <span className="font-medium text-gray-300">{formatDate(report.created_at)}</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Last Updated:</span>
                <span className="font-medium text-gray-300">{formatDate(report.updated_at)}</span>
              </div>
              {report.reviewed_at && (
                <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                  <span className="text-gray-400">Reviewed:</span>
                  <span className="font-medium text-blue-400">{formatDate(report.reviewed_at)}</span>
                </div>
                              )}
              {report.reviewed_by && (
                <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                  <span className="text-gray-400">Reviewed By:</span>
                  <span className="font-medium text-gray-300">{report.reviewed_by}</span>
                </div>
              )}
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Report Type:</span>
                <span className="font-medium text-orange-400">Seller Report</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Severity:</span>
                <span className={`font-medium px-2 py-1 rounded-full text-xs ${severity.bg} ${severity.color}`}>
                  {severity.label}
                </span>
              </div>
            </div>
          </div>

          {/* Evidence Attachments */}
          {(report.evidence_images || report.evidence_urls) && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
                  <FileWarning className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Evidence</h2>
                  <p className="text-sm text-gray-400">Report evidence & attachments</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {report.evidence_images && report.evidence_images.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 mb-2">Images</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {report.evidence_images.map((img, index) => (
                        <a
                          key={index}
                          href={img}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group"
                        >
                          <img
                            src={img}
                            alt={`Evidence ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-600 group-hover:border-purple-500 transition-colors"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                            <Eye className="w-6 h-6 text-white" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {report.evidence_urls && report.evidence_urls.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 mb-2">Links</h3>
                    <div className="space-y-2">
                      {report.evidence_urls.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-3 bg-gray-700/30 border border-gray-600 rounded-lg hover:bg-gray-700/50 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Link className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-300 truncate max-w-xs">
                                {url}
                              </span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-400" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  {selectedAction === 'dismiss' && <CheckCircle className="w-6 h-6 text-green-400" />}
                  {selectedAction === 'warn' && <AlertTriangle className="w-6 h-6 text-yellow-400" />}
                  {selectedAction === 'suspend' && <Shield className="w-6 h-6 text-orange-400" />}
                  {selectedAction === 'reject_application' && <Ban className="w-6 h-6 text-red-400" />}
                  <h2 className="text-xl font-bold text-white">
                    {actionOptions.find(a => a.value === selectedAction)?.label}
                  </h2>
                </div>
                <button
                  onClick={() => setShowActionModal(false)}
                  className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-300">
                  {selectedAction === 'dismiss' && 'This will dismiss the report as invalid. The reporter will be notified.'}
                  {selectedAction === 'warn' && 'Send a formal warning to the seller about their conduct.'}
                  {selectedAction === 'suspend' && 'Temporarily suspend the seller account. They will not be able to access their store during suspension.'}
                  {selectedAction === 'reject_application' && 'Permanently reject the seller application. This action cannot be undone.'}
                </p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const adminNotes = formData.get('adminNotes');
                const severity = formData.get('severity');
                const sendEmail = formData.get('sendEmail') === 'true';
                
                handleAction(selectedAction, adminNotes, severity, sendEmail);
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Admin Notes (Optional)
                    </label>
                    <textarea
                      name="adminNotes"
                      rows="3"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      placeholder="Add internal notes about this action..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Action Severity
                    </label>
                    <select
                      name="severity"
                      defaultValue="medium"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="low" className="bg-gray-800">Low</option>
                      <option value="medium" className="bg-gray-800">Medium</option>
                      <option value="high" className="bg-gray-800">High</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="sendEmail"
                      value="true"
                      id="sendEmail"
                      defaultChecked
                      className="w-4 h-4 bg-gray-700 border-gray-600 rounded focus:ring-orange-500 focus:ring-offset-gray-800"
                    />
                    <label htmlFor="sendEmail" className="text-sm text-gray-300">
                      Send email notification to seller
                    </label>
                  </div>

                  <div className="border-t border-gray-700 pt-6 mt-6">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowActionModal(false)}
                        className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg font-medium transition-colors"
                        disabled={actionLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-[1.02] ${
                          selectedAction === 'dismiss' ? 'bg-green-600 hover:bg-green-700 text-white' :
                          selectedAction === 'warn' ? 'bg-yellow-600 hover:bg-yellow-700 text-white' :
                          selectedAction === 'suspend' ? 'bg-orange-600 hover:bg-orange-700 text-white' :
                          'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                        disabled={actionLoading}
                      >
                        {actionLoading ? (
                          <div className="flex items-center">
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Processing...
                          </div>
                        ) : (
                          `Confirm ${actionOptions.find(a => a.value === selectedAction)?.label}`
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="mt-8 pt-6 border-t border-gray-700">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="text-sm text-gray-500">
            Last updated: {formatDate(report.updated_at)} • Report ID: #{report.report_id}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => fetchReportDetail()}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 rounded-lg font-medium transition-colors flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 rounded-lg font-medium transition-colors flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
            <button
              onClick={() => navigate(`/admin/reports/sellers`)}
              className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Reports
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SellerReportDetail;